import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, NavLink, Routes, Route } from "react-router-dom";
import {
  LayoutDashboard,
  Car as CarIcon,
  Image as ImageIcon,
  User,
  LogOut,
  Plus,
  Edit2,
  Trash2,
  Search,
  CheckCircle2,
  XCircle,
  Save,
  X,
  Upload,
  KeyRound,
  Eye,
  EyeOff,
} from "lucide-react";
import api, { formatPrice, formatKm, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";

const FUEL_OPTIONS = ["Petrol", "Diesel", "Electric", "CNG", "Hybrid", "Petrol+CNG"];
const TRANS_OPTIONS = ["Manual", "Automatic", "AMT", "CVT", "DCT"];

export default function AdminDashboard() {
  if (!localStorage.getItem("fcb_admin_token")) {
    return <Navigate to="/admin/login" replace />;
  }
  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0A] text-[#F5F5DC]">
      <Sidebar />
      <div className="lg:pl-64 min-h-screen">
        <Routes>
          <Route index element={<OverviewTab />} />
          <Route path="cars" element={<CarsTab />} />
          <Route path="gallery" element={<GalleryTab />} />
          <Route path="owner" element={<OwnerTab />} />
          <Route path="settings" element={<SettingsTab />} />
        </Routes>
      </div>
    </div>
  );
}

function Sidebar() {
  const navigate = useNavigate();
  const email = localStorage.getItem("fcb_admin_email");
  const logout = () => {
    localStorage.removeItem("fcb_admin_token");
    localStorage.removeItem("fcb_admin_email");
    toast.success("Signed out");
    navigate("/admin/login");
  };
  const items = [
    { to: "/admin", icon: LayoutDashboard, label: "Overview", end: true },
    { to: "/admin/cars", icon: CarIcon, label: "Cars" },
    { to: "/admin/gallery", icon: ImageIcon, label: "Gallery" },
    { to: "/admin/owner", icon: User, label: "Owner" },
    { to: "/admin/settings", icon: KeyRound, label: "Settings" },
  ];
  return (
    <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 border-r border-white/5 bg-[#0A0A0A] flex-col p-6 z-40">
      <div className="flex items-center gap-3 mb-10">
        <span className="w-10 h-10 rounded-full bg-[#DC143C] flex items-center justify-center font-display text-2xl text-white">F</span>
        <div>
          <div className="font-display text-lg text-[#F5F5DC] leading-tight">Faisal Bazar</div>
          <div className="text-[9px] uppercase tracking-[0.25em] text-[#D4AF37]">Admin Console</div>
        </div>
      </div>
      <nav className="flex-1 space-y-1">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            data-testid={`sidebar-${it.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-all ${
                isActive
                  ? "bg-[#DC143C]/10 text-[#DC143C] border border-[#DC143C]/30"
                  : "text-[#F5F5DC]/70 hover:bg-white/5 hover:text-[#F5F5DC]"
              }`
            }
          >
            <it.icon size={16} />
            {it.label}
          </NavLink>
        ))}
      </nav>
      <div className="mt-auto pt-6 border-t border-white/5">
        <div className="text-xs text-[#A0A0A0] mb-1">Signed in as</div>
        <div className="text-sm text-[#F5F5DC] truncate mb-4">{email}</div>
        <button
          onClick={logout}
          data-testid="admin-logout"
          className="w-full flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-[#F5F5DC]/70 hover:bg-white/5 hover:text-[#DC143C] transition-colors"
        >
          <LogOut size={14} /> Sign Out
        </button>
      </div>
    </aside>
  );
}

function MobileNav() {
  const items = [
    { to: "/admin", icon: LayoutDashboard, label: "Overview", end: true },
    { to: "/admin/cars", icon: CarIcon, label: "Cars" },
    { to: "/admin/gallery", icon: ImageIcon, label: "Gallery" },
    { to: "/admin/owner", icon: User, label: "Owner" },
    { to: "/admin/settings", icon: KeyRound, label: "Settings" },
  ];
  return (
    <div className="lg:hidden flex border-b border-white/5 bg-[#0A0A0A] overflow-x-auto sticky top-0 z-30">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          className={({ isActive }) =>
            `flex items-center gap-2 px-4 py-3 text-xs whitespace-nowrap ${
              isActive ? "text-[#DC143C] border-b-2 border-[#DC143C]" : "text-[#F5F5DC]/70"
            }`
          }
        >
          <it.icon size={14} /> {it.label}
        </NavLink>
      ))}
    </div>
  );
}

function PageWrap({ title, subtitle, actions, children, testId }) {
  return (
    <>
      <MobileNav />
      <div className="p-6 lg:p-10" data-testid={testId}>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="eyebrow mb-2">{subtitle}</div>
            <h1 className="font-display text-3xl text-[#F5F5DC] font-light">{title}</h1>
          </div>
          {actions}
        </div>
        {children}
      </div>
    </>
  );
}

function OverviewTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
  }, []);
  const items = stats ? [
    { l: "Total Cars", v: stats.total_cars, c: "#DC143C" },
    { l: "Available", v: stats.available_cars, c: "#D4AF37" },
    { l: "Sold", v: stats.sold_cars, c: "#A0A0A0" },
    { l: "Sell Enquiries", v: stats.sell_submissions, c: "#25D366" },
    { l: "Contact Messages", v: stats.contact_submissions, c: "#3b82f6" },
  ] : [];
  return (
    <PageWrap title="Overview" subtitle="Dashboard" testId="admin-overview">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {items.map((s) => (
          <div key={s.l} className="premium-card p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0A0]">{s.l}</div>
            <div className="font-display text-4xl mt-3" style={{ color: s.c }}>{s.v}</div>
          </div>
        ))}
      </div>
      <div className="premium-card p-8 mt-8">
        <h3 className="font-display text-2xl text-[#F5F5DC] mb-3">Welcome back!</h3>
        <p className="text-sm text-[#A0A0A0] leading-relaxed max-w-2xl">
          Use the navigation to manage your inventory, gallery and owner profile.
          Newly added cars appear instantly on the public site.
        </p>
      </div>
    </PageWrap>
  );
}

function CarsTab() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => {
    api.get("/cars", { params: { page_size: 50, search: search || undefined } }).then((r) => setItems(r.data.items));
  };

  useEffect(() => { load(); }, [search]);

  const openCreate = () => { setEditing(null); setOpen(true); };
  const openEdit = (car) => { setEditing(car); setOpen(true); };
  const remove = async (id) => {
    if (!window.confirm("Delete this car permanently?")) return;
    await api.delete(`/admin/cars/${id}`);
    toast.success("Car deleted");
    load();
  };

  const toggleStatus = async (car) => {
    const newStatus = car.status === "available" ? "sold" : "available";
    await api.put(`/admin/cars/${car.id}`, { status: newStatus });
    toast.success(`Marked as ${newStatus}`);
    load();
  };

  return (
    <PageWrap
      title="Inventory"
      subtitle={`Cars · ${items.length}`}
      testId="admin-cars"
      actions={
        <button onClick={openCreate} data-testid="admin-add-car" className="btn-primary">
          <Plus size={16} /> Add Car
        </button>
      }
    >
      <div className="relative mb-6 max-w-md">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A0A0A0]" />
        <input
          data-testid="admin-search-cars"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-[#1A1A1A] border border-white/10 rounded-full pl-11 pr-4 py-2.5 text-sm text-[#F5F5DC] focus:border-[#DC143C] outline-none"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-[0.2em] text-[#A0A0A0] border-b border-white/5">
            <tr>
              <th className="py-3 pr-4">Car</th>
              <th className="py-3 pr-4">Year</th>
              <th className="py-3 pr-4">Price</th>
              <th className="py-3 pr-4">Km</th>
              <th className="py-3 pr-4">Status</th>
              <th className="py-3 pr-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-b border-white/5" data-testid={`admin-car-row-${c.id}`}>
                <td className="py-3 pr-4">
                  <div className="flex items-center gap-3">
                    <img src={c.images[0]} alt={c.name} className="w-14 h-10 object-cover rounded-md" />
                    <div className="min-w-0">
                      <div className="text-[#F5F5DC] truncate">{c.name}</div>
                      <div className="text-xs text-[#A0A0A0] truncate">{c.brand} · {c.model}</div>
                    </div>
                  </div>
                </td>
                <td className="py-3 pr-4">{c.year}</td>
                <td className="py-3 pr-4">{formatPrice(c.price)}</td>
                <td className="py-3 pr-4">{formatKm(c.kilometers)}</td>
                <td className="py-3 pr-4">
                  <button
                    onClick={() => toggleStatus(c)}
                    data-testid={`admin-toggle-${c.id}`}
                    className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider transition ${
                      c.status === "available"
                        ? "bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30"
                        : "bg-white/5 text-[#A0A0A0] border border-white/10"
                    }`}
                  >
                    {c.status}
                  </button>
                </td>
                <td className="py-3 pr-4">
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => openEdit(c)}
                      data-testid={`admin-edit-${c.id}`}
                      className="w-8 h-8 rounded-lg border border-white/10 flex items-center justify-center hover:bg-white/5"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => remove(c.id)}
                      data-testid={`admin-delete-${c.id}`}
                      className="w-8 h-8 rounded-lg border border-white/10 flex items-center justify-center hover:bg-[#DC143C]/20 hover:text-[#DC143C]"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <CarForm
          initial={editing}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); load(); }}
        />
      )}
    </PageWrap>
  );
}

function CarForm({ initial, onClose, onSaved }) {
  const empty = {
    name: "", brand: "", model: "", year: 2022, price: 0, kilometers: 0,
    fuel_type: "Petrol", transmission: "Manual", location: "Nagpur",
    color: "", description: "", images: [], status: "available", featured: false,
  };
  const [form, setForm] = useState({ ...empty, ...(initial || {}) });
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return `${BACKEND_URL}${data.url}`;
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = await Promise.all(files.map(uploadFile));
      update("images", [...form.images, ...urls]);
      toast.success(`${urls.length} image(s) uploaded`);
    } catch (err) {
      toast.error("Upload failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const addUrl = () => {
    const url = window.prompt("Image URL");
    if (url) update("images", [...form.images, url]);
  };

  const removeImage = (i) => update("images", form.images.filter((_, idx) => idx !== i));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, year: Number(form.year), price: Number(form.price), kilometers: Number(form.kilometers) };
      if (initial) {
        await api.put(`/admin/cars/${initial.id}`, payload);
        toast.success("Car updated");
      } else {
        await api.post("/admin/cars", payload);
        toast.success("Car added");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="car-form-modal">
      <form onSubmit={save} className="bg-[#0E0E0E] border border-white/10 rounded-2xl max-w-3xl w-full p-6 lg:p-8 my-12 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl text-[#F5F5DC]">{initial ? "Edit Car" : "Add Car"}</h2>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center hover:bg-white/5">
            <X size={16} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput label="Car Name" testId="form-name" value={form.name} onChange={(v) => update("name", v)} required />
          <FormInput label="Brand" testId="form-brand" value={form.brand} onChange={(v) => update("brand", v)} required />
          <FormInput label="Model" testId="form-model" value={form.model} onChange={(v) => update("model", v)} required />
          <FormInput label="Year" testId="form-year" type="number" value={form.year} onChange={(v) => update("year", v)} required />
          <FormInput label="Price (₹)" testId="form-price" type="number" value={form.price} onChange={(v) => update("price", v)} required />
          <FormInput label="Kilometers" testId="form-km" type="number" value={form.kilometers} onChange={(v) => update("kilometers", v)} required />
          <FormSelect label="Fuel Type" testId="form-fuel" value={form.fuel_type} options={FUEL_OPTIONS} onChange={(v) => update("fuel_type", v)} />
          <FormSelect label="Transmission" testId="form-transmission" value={form.transmission} options={TRANS_OPTIONS} onChange={(v) => update("transmission", v)} />
          <FormInput label="Location" testId="form-location" value={form.location} onChange={(v) => update("location", v)} />
          <FormInput label="Color" testId="form-color" value={form.color} onChange={(v) => update("color", v)} />
        </div>

        <div className="mt-4">
          <label className="block text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">Description</label>
          <textarea
            data-testid="form-description"
            rows={3}
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-4 py-3 text-[#F5F5DC] focus:border-[#DC143C] outline-none resize-none"
          />
        </div>

        <div className="mt-4 flex gap-6">
          <label className="flex items-center gap-2 text-sm text-[#F5F5DC]">
            <input
              data-testid="form-featured"
              type="checkbox"
              checked={form.featured}
              onChange={(e) => update("featured", e.target.checked)}
              className="w-4 h-4 accent-[#DC143C]"
            />
            Featured on home
          </label>
          <FormSelect label="Status" testId="form-status" value={form.status} options={["available", "sold"]} onChange={(v) => update("status", v)} />
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">Images ({form.images.length})</label>
            <div className="flex gap-2">
              <button type="button" onClick={addUrl} className="text-xs px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5">Add URL</button>
              <label data-testid="form-upload-label" className="text-xs px-3 py-1.5 rounded-full bg-[#DC143C] hover:bg-[#ED1C24] text-white cursor-pointer flex items-center gap-1">
                <Upload size={12} /> {uploading ? "Uploading…" : "Upload"}
                <input data-testid="form-upload-input" type="file" multiple accept="image/*" className="hidden" onChange={onFiles} disabled={uploading} />
              </label>
            </div>
          </div>
          {form.images.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {form.images.map((url, i) => (
                <div key={url} className="relative aspect-square rounded-lg overflow-hidden border border-white/10">
                  <img src={url} alt={`img ${i + 1}`} className="absolute inset-0 w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-[#DC143C]"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={busy} data-testid="form-save" className="btn-primary">
            <Save size={16} /> {busy ? "Saving…" : "Save Car"}
          </button>
        </div>
      </form>
    </div>
  );
}

function FormInput({ label, value, onChange, type = "text", testId, required }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F5F5DC] focus:border-[#DC143C] outline-none"
      />
    </div>
  );
}

function FormSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">{label}</label>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F5F5DC] focus:border-[#DC143C] outline-none"
      >
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );
}

function GalleryTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", image_url: "", category: "showroom" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/gallery").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return `${BACKEND_URL}${data.url}`;
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadFile(file);
      setForm((f) => ({ ...f, image_url: url }));
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!form.title || !form.image_url) {
      toast.error("Title and image are required");
      return;
    }
    await api.post("/admin/gallery", form);
    toast.success("Added");
    setForm({ title: "", image_url: "", category: "showroom" });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this image?")) return;
    await api.delete(`/admin/gallery/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <PageWrap title="Gallery" subtitle="Showroom & Delivery" testId="admin-gallery">
      <form onSubmit={add} className="premium-card p-6 mb-8 grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="gallery-add-form">
        <FormInput label="Title" testId="gallery-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">Image URL</label>
          <div className="flex gap-2">
            <input
              data-testid="gallery-url"
              value={form.image_url}
              onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              placeholder="paste URL or upload"
              className="flex-1 bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F5F5DC] focus:border-[#DC143C] outline-none"
            />
            <label className="text-xs px-3 py-2.5 rounded-lg bg-[#DC143C] text-white cursor-pointer flex items-center gap-1">
              <Upload size={12} />
              <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={busy} />
            </label>
          </div>
        </div>
        <FormSelect label="Category" testId="gallery-category" value={form.category} options={["showroom", "delivery"]} onChange={(v) => setForm({ ...form, category: v })} />
        <button type="submit" className="btn-primary self-end justify-center" data-testid="gallery-add-submit">
          <Plus size={16} /> Add
        </button>
      </form>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((g) => (
          <div key={g.id} className="premium-card overflow-hidden" data-testid={`gallery-row-${g.id}`}>
            <img src={g.image_url} alt={g.title} className="w-full aspect-square object-cover" />
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">{g.category}</div>
              <div className="text-sm text-[#F5F5DC] mt-1 truncate">{g.title}</div>
              <button
                onClick={() => remove(g.id)}
                data-testid={`gallery-delete-${g.id}`}
                className="mt-3 w-full text-xs flex items-center justify-center gap-1 py-2 rounded-full border border-white/10 hover:bg-[#DC143C]/20 hover:text-[#DC143C]"
              >
                <Trash2 size={12} /> Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </PageWrap>
  );
}

function OwnerTab() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/owner").then((r) => setForm(r.data)); }, []);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_url: `${BACKEND_URL}${data.url}` }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/owner", {
        ...form,
        years_experience: Number(form.years_experience),
        cars_delivered: Number(form.cars_delivered),
        happy_customers: Number(form.happy_customers),
      });
      toast.success("Owner profile updated");
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (!form) return <PageWrap title="Owner" subtitle="Profile" testId="admin-owner"><div className="text-[#A0A0A0]">Loading…</div></PageWrap>;

  return (
    <PageWrap title="Owner Profile" subtitle="About Page" testId="admin-owner">
      <form onSubmit={save} className="premium-card p-8 max-w-3xl">
        <div className="flex items-start gap-6 mb-8">
          <div className="relative w-32 h-32 rounded-2xl overflow-hidden border border-white/10 bg-[#1A1A1A]">
            {form.photo_url && <img src={form.photo_url} alt={form.name} className="w-full h-full object-cover" />}
          </div>
          <div>
            <label className="btn-secondary cursor-pointer" data-testid="owner-upload-label">
              <Upload size={14} /> {busy ? "Uploading…" : "Change Photo"}
              <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
            </label>
            <p className="text-xs text-[#A0A0A0] mt-3">JPG / PNG, max 10MB</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput label="Name" testId="owner-name-input" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
          <FormInput label="Title" testId="owner-title-input" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
          <FormInput label="Years Experience" testId="owner-years" type="number" value={form.years_experience} onChange={(v) => setForm({ ...form, years_experience: v })} />
          <FormInput label="Cars Delivered" testId="owner-cars" type="number" value={form.cars_delivered} onChange={(v) => setForm({ ...form, cars_delivered: v })} />
          <FormInput label="Happy Customers" testId="owner-customers" type="number" value={form.happy_customers} onChange={(v) => setForm({ ...form, happy_customers: v })} />
        </div>

        <div className="mt-4">
          <label className="block text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">Bio</label>
          <textarea
            data-testid="owner-bio-input"
            rows={5}
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-4 py-3 text-[#F5F5DC] focus:border-[#DC143C] outline-none resize-none"
          />
        </div>

        <button type="submit" disabled={busy} className="btn-primary mt-6" data-testid="owner-save">
          <Save size={16} /> Save Profile
        </button>
      </form>
    </PageWrap>
  );
}

function SettingsTab() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState({ c: false, n: false, cf: false });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const strength = (() => {
    if (!next) return { label: "", pct: 0, color: "#A0A0A0" };
    let score = 0;
    if (next.length >= 8) score++;
    if (next.length >= 12) score++;
    if (/[A-Z]/.test(next) && /[a-z]/.test(next)) score++;
    if (/\d/.test(next)) score++;
    if (/[^A-Za-z0-9]/.test(next)) score++;
    const labels = ["Very weak", "Weak", "Fair", "Good", "Strong", "Very strong"];
    const colors = ["#EF4444", "#F97316", "#EAB308", "#84CC16", "#22C55E", "#16A34A"];
    return { label: labels[score], pct: (score / 5) * 100, color: colors[score] };
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("New password and confirmation don't match");
      return;
    }
    if (next === current) {
      toast.error("New password must be different from current");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated. Please sign in again.");
      localStorage.removeItem("fcb_admin_token");
      localStorage.removeItem("fcb_admin_email");
      setTimeout(() => navigate("/admin/login"), 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change password");
    } finally {
      setBusy(false);
    }
  };

  const Field = ({ label, value, onChange, keyName, testId }) => (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">{label}</label>
      <div className="relative">
        <input
          data-testid={testId}
          type={show[keyName] ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          autoComplete="new-password"
          className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-4 py-3 pr-11 text-sm text-[#F5F5DC] focus:border-[#DC143C] outline-none"
        />
        <button
          type="button"
          onClick={() => setShow((s) => ({ ...s, [keyName]: !s[keyName] }))}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A0A0] hover:text-[#D4AF37]"
          tabIndex={-1}
          aria-label={show[keyName] ? "Hide password" : "Show password"}
        >
          {show[keyName] ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );

  return (
    <PageWrap title="Settings" subtitle="Account" testId="admin-settings">
      <form onSubmit={submit} className="premium-card p-8 max-w-2xl" data-testid="change-password-form">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-[#DC143C]/10 border border-[#DC143C]/30 flex items-center justify-center">
            <KeyRound size={18} className="text-[#DC143C]" />
          </div>
          <div>
            <h2 className="font-display text-2xl text-[#F5F5DC]">Change Password</h2>
            <p className="text-xs text-[#A0A0A0] mt-0.5">
              Choose a strong, unique password to secure your admin account.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <Field label="Current Password" value={current} onChange={setCurrent} keyName="c" testId="cp-current" />
          <Field label="New Password" value={next} onChange={setNext} keyName="n" testId="cp-new" />

          {next && (
            <div data-testid="cp-strength">
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div
                  className="h-full transition-all duration-300"
                  style={{ width: `${strength.pct}%`, background: strength.color }}
                />
              </div>
              <div className="flex justify-between mt-2 text-[10px] uppercase tracking-[0.2em]">
                <span style={{ color: strength.color }}>{strength.label}</span>
                <span className="text-[#A0A0A0]">min. 8 characters</span>
              </div>
            </div>
          )}

          <Field label="Confirm New Password" value={confirm} onChange={setConfirm} keyName="cf" testId="cp-confirm" />

          {confirm && next !== confirm && (
            <div className="text-xs text-[#EF4444] flex items-center gap-1.5" data-testid="cp-mismatch">
              <XCircle size={12} /> Passwords do not match
            </div>
          )}
          {confirm && next === confirm && next.length >= 8 && (
            <div className="text-xs text-[#22C55E] flex items-center gap-1.5">
              <CheckCircle2 size={12} /> Passwords match
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-wrap gap-3 items-center justify-between">
          <p className="text-xs text-[#A0A0A0]">
            You'll be signed out and asked to log in again after the change.
          </p>
          <button
            type="submit"
            disabled={busy}
            data-testid="cp-submit"
            className="btn-primary"
          >
            <Save size={16} /> {busy ? "Updating…" : "Update Password"}
          </button>
        </div>
      </form>
    </PageWrap>
  );
}

